import { useCallback, useMemo, useState } from "react";
import { Idea, IdeaAccessors, w2d2dApiClient } from "../../../../w2d2d-api-client";
import { Box, Button, Typography, useTheme } from "@mui/material";
import UsersChooser from "../../../components/users/UsersChooser.tsx";
import { formatDateTimeRange } from "../../../../utils/datetime-range-util.ts";

import { useConnectionsControl } from "../../../hooks/useConnectionsControl.tsx";

interface AnnounceIdeaPageProps {
    idea: Idea;
    accessors: IdeaAccessors;
    onClose: () => void;
}

export default function AnnounceIdeaPage({ idea, accessors, onClose }: AnnounceIdeaPageProps) {
    const theme = useTheme();
    const { connections } = useConnectionsControl();
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const disabledUserIds = useMemo(() => {
        const accepted = idea.accepted || [];
        const interested = idea.interested || [];
        const rejected = idea.rejected || [];
        return [...accepted, ...interested, ...rejected];
    }, [idea]);

    const userIdsToDisplay = useMemo(() => {
        if (!connections) {
            return [];
        }
        if (accessors.network) {
            return connections;
        }
        const allowed = accessors.userIds || [];
        return connections.filter(id => allowed.includes(id));
    }, [accessors, connections]);

    const handleSelectionChange = useCallback((ids: string[]) => {
        setSelectedUserIds(ids);
    }, []);

    const handleSend = useCallback(async () => {
        await w2d2dApiClient.notifyIdea({
            id: idea.id,
            userIds: selectedUserIds
        });
        onClose();
    }, [idea, selectedUserIds, onClose]);

    const dateText = useMemo(() => {
        if (!idea.startsAt) return null;
        return formatDateTimeRange({
            startDate: new Date(idea.startsAt),
            endDate: idea.endsAt ? new Date(idea.endsAt) : undefined
        });
    }, [idea]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    {dateText && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                fontSize: '0.75rem'
                            }}
                        >
                            {dateText}
                        </Typography>
                    )}
                    <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                        {idea.title}
                    </Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                    Wähle Freunde aus, die du benachrichtigen möchtest.
                </Typography>
                <UsersChooser
                    userIds={userIdsToDisplay}
                    disabledUserIds={disabledUserIds}
                    selectedUserIds={selectedUserIds}
                    onSelectionChange={handleSelectionChange}
                />
            </Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
                <Button onClick={onClose} color="inherit">
                    Abbrechen
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    color="primary"
                    disabled={selectedUserIds.length === 0}
                >
                    Senden
                </Button>
            </Box>
        </Box>
    );
}
